import type { PageProps } from "gatsby";
import { graphql } from "gatsby";
import React from "react";

import { NotFoundPageQuery } from "../../graphql-types";
import { Breadcrumb } from "../components/Breadcrumb";
import { BreadcrumbItem } from "../components/BreadcrumbItem";
import { default as GlobalHead } from "../components/head";
import Layout from "../components/layout";
import PromoCard from "../components/PromoCard";
import PromoCardGrid from "../components/PromoCardGrid";
import { useLexicon } from "../context/LexiconContext";

export type NotFoundPageProps = Pick<PageProps<NotFoundPageQuery>, "data">;

const NotFoundPage: React.FC<NotFoundPageProps> = ({ data: { website } }) => {
  const lexicon = useLexicon();
  if (!website) {
    throw new Error("Could not query data");
  }

  const { absolutePath, legals, messages } = website;
  return (
    <Layout
      {...{
        absolutePath,
        home: website,
        legals,
        metaDescription: lexicon.pageNotFoundText,
        pageTitle: "404",
      }}
    >
      <Breadcrumb>
        <BreadcrumbItem isCurrent label="404" />
      </Breadcrumb>
      <h1 className="py-20 my-0 text-center title">
        {lexicon.pageNotFoundText}
      </h1>
      {messages.length > 0 && (
        <section data-cy="products" className="pb-20 products bg-gray-200">
          <h2 className="text-center title">{lexicon.products}</h2>
          <PromoCardGrid>
            {messages.map(({ hero, path, teaserImage, title }) => (
              <PromoCard
                key={path}
                asset={teaserImage || hero}
                href={path}
                title={title}
              />
            ))}
          </PromoCardGrid>
        </section>
      )}
    </Layout>
  );
};

export const query = graphql`
  query NotFoundPage {
    website {
      ...homeFields
      messages {
        path
        title
        hero {
          ...PromoCardHeroImage
        }
        teaserImage {
          ...PromoCardTeaserImage
        }
      }
    }
  }
`;

export default NotFoundPage;

export const Head: React.FC<NotFoundPageProps> = ({ data: { website } }) => {
  const lexicon = useLexicon();
  if (!website) {
    throw new Error("Could not query data");
  }

  const { absolutePath, legals } = website;
  return (
    <GlobalHead
      {...{
        absolutePath,
        home: website,
        legals,
        metaDescription: lexicon.pageNotFoundText,
        pageTitle: "404",
      }}
    />
  );
};
